import { Box, Button, Container, Divider, Grid, Hidden, Typography } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { ReactComponent as Dots } from 'assets/dots.svg';
import { CategoryList, JobGridItem, Link, Spinner } from 'components';
import { GoogleAnaliticsEvent } from 'enum';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-material-ui';
import {
    Job,
    useBookmarkJobMutation,
    useHomeStatisticsQuery,
    useNewJobsQuery,
    useSelectedJobsQuery,
    useTopCategoriesQuery,
} from 'graphql/generated';
import React from 'react';
import CountUp from 'react-countup';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { helmetJsonLdProp } from 'react-schemaorg';
import { logAnalyticsEvent } from 'ui-services/analytics.ui-service';
import browserHistory from 'utils/browser-utils';
import { useStyles } from './home.style';

export const Home: React.FC = () => {
    const classes = useStyles();

    const { t } = useTranslation();
    const { data } = useHomeStatisticsQuery({
        fetchPolicy: 'cache-and-network',
    });
    const { data: categoryDate, loading: categoryLoading } = useTopCategoriesQuery({
        fetchPolicy: 'cache-and-network',
    });
    const { data: selectedJobsData, loading: selectedJobsLoading } = useSelectedJobsQuery({
        fetchPolicy: 'cache-and-network',
    });
    const { data: newJobsData, loading: newJobsLoading } = useNewJobsQuery({
        fetchPolicy: 'cache-and-network',
    });

    const [bookmarkJob] = useBookmarkJobMutation();

    const selectedJobs = selectedJobsData?.selectedJobs.edges || [];
    const newJobs = newJobsData?.jobs.edges || [];
    const topCategories = categoryDate?.topCategories.edges || [];
    const jobCount = data?.jobCount || 0;
    const jobSeekerCount = data?.jobseekers.totalCount || 0;
    const companyCount = data?.companyCount || 0;

    return (
        <Box className={classes.root}>
            <Helmet
                script={[
                    helmetJsonLdProp({
                        '@context': 'https://schema.org',
                        '@type': 'WebSite',
                        url: 'https://staffy.az/',
                        potentialAction: {
                            '@type': 'SearchAction',
                            target: 'https://staffy.az/jobs?searchText={search_term_string}',
                            'query-input': 'required name=search_term_string',
                        },
                    }),
                ]}
            >
                <link rel="preload" as="image" href="https://s3.mediusoft.com/assets/staffy/hero.jpg" />
            </Helmet>
            <Box className={classes.hero}>
                <Container
                    maxWidth="lg"
                    style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}
                >
                    <Dots className={classes.dots} />
                    <Box display="flex" flexGrow={1}>
                        <Box width="100%" display="flex" alignItems="center">
                            <Box display="flex" flexDirection="column" height="80%" flexGrow={1}>
                                <Box flexGrow={1} display="flex" justifyContent="center" flexDirection="column">
                                    <Box
                                        className={classes.heroText}
                                        dangerouslySetInnerHTML={{ __html: t('homePage:title') }}
                                    />
                                    <Typography variant="body1" color="textSecondary" style={{ fontWeight: 500 }}>
                                        {t('homePage:subtitle')}
                                    </Typography>
                                    <Formik
                                        initialValues={{ searchText: '' }}
                                        onSubmit={(formData) => {
                                            const location = formData.searchText
                                                ? `/jobs?searchText=${encodeURIComponent(formData.searchText)}`
                                                : '/jobs';
                                            browserHistory.push(location);
                                        }}
                                    >
                                        {({ handleSubmit, values }) => {
                                            return (
                                                <Form onSubmit={handleSubmit}>
                                                    <Box className={classes.search}>
                                                        <Field
                                                            component={TextField}
                                                            fullWidth
                                                            name="searchText"
                                                            variant="outlined"
                                                            placeholder={t('homePage:jobTitleOrKeywords')}
                                                            autoComplete="off"
                                                            className={classes.input}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <SearchIcon
                                                                        style={{ marginRight: 8, opacity: 0.7 }}
                                                                    />
                                                                ),
                                                            }}
                                                        />
                                                        <Hidden smDown>
                                                            <Link
                                                                to={
                                                                    values.searchText
                                                                        ? `/jobs?searchText=${encodeURIComponent(
                                                                              values.searchText,
                                                                          )}`
                                                                        : '/jobs'
                                                                }
                                                            >
                                                                <Button
                                                                    className={classes.searchButton}
                                                                    type="submit"
                                                                    variant="contained"
                                                                    color="primary"
                                                                >
                                                                    {t('common:search')}
                                                                </Button>
                                                            </Link>
                                                        </Hidden>
                                                    </Box>
                                                </Form>
                                            );
                                        }}
                                    </Formik>
                                </Box>
                                <Box>
                                    <ul className={classes.statistics}>
                                        <li>
                                            <Link to="/companies">
                                                <CountUp end={companyCount} delay={0} duration={1}>
                                                    {({ countUpRef }) => <strong ref={countUpRef} />}
                                                </CountUp>
                                                <span>{t('homePage:companyCount')}</span>
                                            </Link>
                                        </li>
                                        <Divider className={classes.verticalDivider} orientation="vertical" />
                                        <li>
                                            <Link to="/jobs">
                                                <CountUp end={jobCount} delay={0} duration={2}>
                                                    {({ countUpRef }) => <strong ref={countUpRef} />}
                                                </CountUp>
                                                <span>{t('homePage:jobCount')}</span>
                                            </Link>
                                        </li>
                                        <Divider className={classes.verticalDivider} orientation="vertical" />
                                        <li>
                                            <Link to="/jobseekers">
                                                <CountUp end={jobSeekerCount} delay={0} duration={3}>
                                                    {({ countUpRef }) => <strong ref={countUpRef} />}
                                                </CountUp>
                                                <span>{t('homePage:jobSeekerCount')}</span>
                                            </Link>
                                        </li>
                                    </ul>
                                </Box>
                            </Box>
                            <Box className={classes.heroImage}>
                                <img src="https://s3.mediusoft.com/assets/staffy/hero.jpg" alt="hero" />
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>
            <Box className={classes.jobs} style={{ paddingTop: 0 }}>
                <Container maxWidth="lg">
                    <Box className={classes.headline}>
                        <h3>{t('homePage:selectedJobs')}</h3>
                    </Box>
                    {selectedJobsLoading ? (
                        <Spinner containerClassName={classes.spinner} />
                    ) : (
                        <Grid container spacing={2}>
                            {selectedJobs.map(({ node: job }) => {
                                return (
                                    <Grid item xs={12} sm={6} lg={4} key={job.id}>
                                        <JobGridItem bookmarkJob={bookmarkJob} job={job as Job} />
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}

                    <Box className={classes.seeMore}>
                        <Link to="jobs">
                            <Button
                                color="primary"
                                variant="outlined"
                                onClick={() => {
                                    logAnalyticsEvent(GoogleAnaliticsEvent.BROWSEALLJOBS_CLICKED);
                                }}
                            >
                                {t('homePage:browseAllJobs')}
                            </Button>
                        </Link>
                    </Box>
                </Container>
            </Box>
            <div className={classes.categories}>
                <Container maxWidth="lg">
                    <Box className={classes.headline}>
                        <h3>{t('homePage:popularJobCategories')}</h3>
                    </Box>
                    <Box className={classes.categoriesWrapper}>
                        {categoryLoading ? (
                            <Spinner containerClassName={classes.spinner} />
                        ) : (
                            <CategoryList categories={topCategories} />
                        )}
                    </Box>
                </Container>
            </div>
            <Box className={classes.jobs}>
                <Container maxWidth="lg">
                    <Box className={classes.headline}>
                        <h3>{t('homePage:newJobs')}</h3>
                    </Box>
                    {newJobsLoading ? (
                        <Spinner containerClassName={classes.spinner} />
                    ) : (
                        <Grid container spacing={2}>
                            {newJobs.slice(0, 18).map(({ node: job }) => {
                                return (
                                    <Grid item xs={12} sm={6} lg={4} key={job.id}>
                                        <JobGridItem bookmarkJob={bookmarkJob} job={job as Job} />
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )}
                    <Box className={classes.seeMore}>
                        <Link to="jobs">
                            <Button
                                color="primary"
                                variant="outlined"
                                onClick={() => {
                                    logAnalyticsEvent(GoogleAnaliticsEvent.BROWSEALLJOBS_CLICKED);
                                }}
                            >
                                {t('homePage:browseAllJobs')}
                            </Button>
                        </Link>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};
